import { useMemo } from 'react'
import {
  ProductFeatureV3,
  RoundRobinExpectedHostV2,
} from '@chilipiper/api-client/src/user-availability'
import { SanitizedAssigneeSelection } from '@chilipiper/api-client/src/handoff'
import { UserSlugResolution } from '@chilipiper/api-client/src/firerouter'
import { AlwaysInvitedUser } from '@chilipiper/api-client/src/team-scheduling'
import { LinkResourceEnhanced, OwnershipLinkRoutingResult, WithDistribution } from '../types'
import { BookerInvitationSettings } from '../../load-availability-options'
import { useOwnershipRoutingWithLinkResource } from './use-ownership-routing-hooks'
import { useRoundRobinRouting } from './use-round-robin-routing'

export interface UnifiedRoutingParams {
  /** Tenant ID for ownership routing API call */
  tenantId: string
  /** Link resource with flattened properties */
  linkResource: LinkResourceEnhanced | undefined
  /** Product feature from meeting attempt */
  productFeature: ProductFeatureV3 | undefined
  /** Raw search params (needed for ownership CRM explicits) */
  searchParams: URLSearchParams
  /** Smart parameter ID for guest resolution */
  smartParameterId?: string | null
  /** Selected assignee ID from search params */
  selectedAssigneeIds?: string[]
  meetingAttemptBookerId?: string
  bookerFromSmartParameter?: UserSlugResolution
}

export interface RoundRobinRoutingResult {
  type: 'RoundRobinSchedulingLink'
  expectedHost: RoundRobinExpectedHostV2
  routingResult: WithDistribution
  primaryGuestEmail?: string
  bookerInvitationSettings?: BookerInvitationSettings
  alwaysInvitedUsers?: AlwaysInvitedUser[]
}

export interface OwnershipRoutingResult {
  type: 'OwnershipSchedulingLink'
  expectedHost: RoundRobinExpectedHostV2
  routingResult: OwnershipLinkRoutingResult
  primaryGuestEmail?: string
  bookerInvitationSettings?: BookerInvitationSettings
  alwaysInvitedUsers?: AlwaysInvitedUser[]
}

export interface HandoffRoutingResult {
  type: 'HandoffRouter'
  selectedAssignees: SanitizedAssigneeSelection[]
  isFlexible: boolean
  primaryGuestEmail?: string
}

export type UnifiedRoutingResult =
  | RoundRobinRoutingResult
  | OwnershipRoutingResult
  | HandoffRoutingResult
  | undefined

/**
 * Unified routing hook that wraps all link-specific routing hooks.
 *
 * This hook conditionally calls the appropriate routing hook based on link type and product
 * feature, providing a consistent API for routing across all link types.
 *
 * @example
 *   ;```tsx
 *   const routing = useUnifiedRouting({
 *     tenantId,
 *     linkResource,
 *     productFeature,
 *     bookerSettings,
 *     searchParams,
 *     smartParameterId,
 *     selectedAssigneeIds,
 *   })
 *
 *   if (routing?.type === 'RoundRobinSchedulingLink') {
 *     const { expectedHost, routingResult } = routing
 *   }
 *   ```
 */
export const useUnifiedRouting = ({
  tenantId,
  linkResource,
  productFeature,
  smartParameterId,
  selectedAssigneeIds,
  meetingAttemptBookerId,
  bookerFromSmartParameter,
}: UnifiedRoutingParams): UnifiedRoutingResult => {
  // Determine if we should call each routing hook
  const shouldUseRoundRobin =
    linkResource?.type === 'RoundRobinSchedulingLink' &&
    productFeature?.type === 'RoundRobinSchedulingLink'

  const shouldUseOwnership =
    linkResource?.type === 'OwnershipSchedulingLink' &&
    productFeature?.type === 'OwnershipSchedulingLink'

  const shouldUseHandoff =
    linkResource?.type === 'HandoffRouter' && productFeature?.type === 'HandoffRouting'

  // Call round-robin routing hook (conditionally enabled)
  const roundRobinRouting = useRoundRobinRouting({
    tenantId,
    roundRobinSchedulingLink: shouldUseRoundRobin ? linkResource : undefined,
    smartParameterId,
    productFeature: shouldUseRoundRobin ? productFeature : undefined,
    meetingAttemptBookerId,
    selectedAssigneeIds,
    bookerFromSmartParameter,
  })

  // Call ownership routing hook (conditionally enabled)
  const ownershipRouting = useOwnershipRoutingWithLinkResource({
    tenantId: shouldUseOwnership ? tenantId : undefined,
    linkResource: shouldUseOwnership ? linkResource : undefined,
    smartParameterId,
    productFeature: shouldUseOwnership ? productFeature : undefined,
    selectedAssigneeIds,
    meetingAttemptBookerId,
    bookerFromSmartParameter,
  })

  // Handle handoff routing (computed, not a hook)
  const handoffRouting = useMemo(() => {
    if (!shouldUseHandoff) {
      return undefined
    }

    const selectedAssignees = linkResource.selectedAssignees
    if (!selectedAssignees || selectedAssignees.length === 0) {
      return undefined
    }

    // Check if any assignee is flexible (AvailabilityOptimised with multiple users)
    const isFlexible = selectedAssignees.some(
      assignee =>
        assignee.type === 'FromDistribution' &&
        assignee.algorithm === 'AvailabilityOptimised' &&
        assignee.users.length > 1
    )

    const primaryGuestEmail = linkResource.primaryGuest?.email

    return {
      selectedAssignees,
      isFlexible,
      primaryGuestEmail,
    }
  }, [shouldUseHandoff, linkResource])

  // Return the appropriate routing result based on type
  if (shouldUseRoundRobin && roundRobinRouting) {
    return {
      type: 'RoundRobinSchedulingLink',
      expectedHost: roundRobinRouting.expectedHost,
      routingResult: roundRobinRouting.roundRobinLinkRoutingResult,
      primaryGuestEmail: roundRobinRouting.primaryGuestEmail,
      bookerInvitationSettings: roundRobinRouting.bookerInvitationSettings,
      alwaysInvitedUsers: roundRobinRouting.alwaysInvitedUsers,
    }
  }

  if (shouldUseOwnership && ownershipRouting) {
    return {
      type: 'OwnershipSchedulingLink',
      expectedHost: ownershipRouting.expectedHost,
      routingResult: ownershipRouting.ownershipLinkRoutingResult,
      primaryGuestEmail: ownershipRouting.primaryGuestEmail,
      bookerInvitationSettings: ownershipRouting.bookerInvitationSettings,
      alwaysInvitedUsers: ownershipRouting.alwaysInvitedUsers,
    }
  }

  if (shouldUseHandoff && handoffRouting) {
    return {
      type: 'HandoffRouter',
      selectedAssignees: handoffRouting.selectedAssignees,
      isFlexible: handoffRouting.isFlexible,
      primaryGuestEmail: handoffRouting.primaryGuestEmail,
    }
  }

  return undefined
}
